import { useState } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { Accordion, ButtonText } from '@morressier/alembic';
import { faBell, faClock, faRefresh } from '@fortawesome/pro-regular-svg-icons';

import { calculateDaysToDeadline } from 'src/utils/deadlines';
import { useReviewData } from 'src/contexts/ReviewDataProvider';
import { RequestExtensionModal } from 'src/components/Review/RequestExtensionModal';
import { REVIEW_DETAILS_TAB, ReviewDetailsTab } from 'src/pages/Review';
import Divider from 'src/components/Divider';
import { IconBadge } from 'src/components/IconBadge';
import AdditionalInformationWrapper from '../../AdditionalInformationWrapper';
import { CoReviewHighlight } from '../CoReviewHighlight';
import { timeFormatDeadlineDate } from '../../../../../utils/date';
import { ReviewScoreHighlight } from '../ReviewScoreHighlight';

export const ReviewerRevisionAssessmentInfo = ({
  setActiveTab
}: {
  setActiveTab: (tab: ReviewDetailsTab) => void;
}) => {
  const { review, hasSubmittedReviewInCurrentRound } = useReviewData();
  const [isExtensionModalOpen, setIsExtensionModalOpen] =
    useState<boolean>(false);

  const deadline = review.workflow.currentDeadline;
  const daysToDeadline = calculateDaysToDeadline(new Date(deadline ?? ''));
  const deadlineApproaching = daysToDeadline <= 7;
  const deadlineOverdue = daysToDeadline <= 0;
  const extensionRequested =
    review.workflow.deadlineExtensions?.status === 'requested';

  return (
    <AdditionalInformationWrapper>
      <RequestExtensionModal
        isOpen={isExtensionModalOpen}
        onClose={() => setIsExtensionModalOpen(false)}
        remainingDeadlineAutoExtension={
          review?.workflow.deadlineExtensions?.remainingAutoextensions
        }
      />

      <Accordion
        chakraAccordionProps={{
          width: '100%',
          defaultIndex: 0,
          allowToggle: true
        }}
        items={[
          {
            title: (
              <Flex alignItems={'flex-start'} gap={2}>
                <IconBadge icon={faBell} variant="neutral" />
                <Flex flexDirection={'column'} marginLeft={2}>
                  <Text textStyle="headingItem">Highlights</Text>
                  <Text textStyle="textContext">
                    Review key updates and assessment information for this
                    revision phase.
                  </Text>
                </Flex>
              </Flex>
            ),
            content: (
              <Flex flexDirection="column" width="100%" gap={4}>
                {review.workflow.currentState === 'revisionAssessment' &&
                  !hasSubmittedReviewInCurrentRound() && (
                    <>
                      <Flex paddingY={2} alignItems="center" gap={2}>
                        <Flex flexDirection="column">
                          <IconBadge icon={faRefresh} variant="attention" />
                        </Flex>
                        <Flex flex={1} flexDirection="column">
                          <Text textStyle="headingItem">
                            Revision Undergoing
                          </Text>
                          <Text>
                            This review is a part of a revision round and
                            contains important feedback from the Author and
                            Editor.
                          </Text>
                        </Flex>
                        <ButtonText
                          onClick={() => {
                            setActiveTab?.(REVIEW_DETAILS_TAB.REVIEW);
                          }}
                        >
                          View Feedback
                        </ButtonText>
                      </Flex>
                      {Divider}
                    </>
                  )}

                {deadline && (
                  <Flex width="100%" gap={3} alignItems="center">
                    <IconBadge
                      icon={faClock}
                      variant={deadlineApproaching ? 'caution' : 'attention'}
                    />
                    <Flex flexDirection="column" flex={1}>
                      <Flex gap={2}>
                        <Text textStyle="headingItem">
                          Deadline on {timeFormatDeadlineDate(deadline)}
                        </Text>
                        <ButtonText
                          marginLeft="auto"
                          isDisabled={
                            !!extensionRequested ||
                            hasSubmittedReviewInCurrentRound()
                          }
                          onClick={() => setIsExtensionModalOpen(true)}
                        >
                          Request Extension
                        </ButtonText>
                      </Flex>
                      <Text textStyle="textContext">
                        {review?.workflow.deadlineExtensions?.status ===
                          'denied' &&
                          'The editors have rejected your last extension request.'}
                        {deadlineOverdue
                          ? `Your review is overdue by ${Math.abs(
                              daysToDeadline
                            )} days.`
                          : `You have ${daysToDeadline} days to complete your assessment.`}
                      </Text>
                    </Flex>
                  </Flex>
                )}

                <ReviewScoreHighlight setActiveTab={setActiveTab} />
                <CoReviewHighlight />
              </Flex>
            )
          }
        ]}
      />
    </AdditionalInformationWrapper>
  );
};
